import {
  fetchPortsDataWithModel,
  sendBatchRequest,
  fetchSharedPortData,
  // fetchSharedPortGroupData,
  updateModbusData,
  fetchModbusSlaveData,
  pushModbusSlaveData,
  updateModbusSlaveData,
  removeModbusSlaveData,
  fetchLogDownloadLink,
  fetchLogData,
} from '@/api/api.APP_TARGET';
import Logger from '@/js/logger';

const getPortsData = ({ state, commit }, { dev_model, dev_id }) => {
  if (state.promise[dev_id]) return state.promise[dev_id];
  const fetchPromise = fetchPortsDataWithModel(dev_model, dev_id).then(
    (portsData) => {
      commit('SET_PORTS_DATA', { data: [portsData], uid: dev_id });
      commit('SET_PROMISE', { promise: null, uid: dev_id });
    }
  );
  commit('SET_PROMISE', { promise: fetchPromise, uid: dev_id });
  return fetchPromise;
};

const getPortGroup = ({ state, commit }, list) => {
  const realList = [];
  list.forEach(({ dev_id, dev_model }) => {
    if (!state.promise[dev_id]) realList.push({ dev_id, dev_model });
  });

  if (!realList.length) return Promise.resolve([]);

  const queryQueue = [];
  const DEV_PER_QUERY = process.env.DEVICES_PER_BATCH;

  for (
    let chunk = 0, len = realList.length / DEV_PER_QUERY;
    chunk < len;
    chunk++
  ) {
    queryQueue.push(
      realList.slice(
        chunk * DEV_PER_QUERY,
        chunk * DEV_PER_QUERY + DEV_PER_QUERY
      )
    );
  }

  return queryQueue
    .reduce((sequence, queryList) => {
      return sequence.then(() => {
        const requests = queryList.map((el) => ({
          ...fetchPortsDataWithModel(el.dev_model, el.dev_id, true),
          commit: 'SET_PORTS_DATA',
        }));
        Logger.info({ requests });
        return sendBatchRequest(
          requests.map(({ relative_url, method, body }) => ({
            relative_url,
            method,
            body,
          }))
        ).then((res) => {
          res.forEach((r) => {
            const req = requests.find(
              (request) => request.relative_url === r.relative_url
            );
            if (!req) return;
            const response = Object.assign({}, req, r);
            if (response.passcode.indexOf(response.status) !== -1) {
              Logger.info('[HTTP Port res]', response);

              commit(response.commit, {
                data: [
                  typeof response.body === 'string'
                    ? JSON.parse(response.body)
                    : response.body,
                ],
                uid: response.body.dev_id,
              });
            }
          });
        });
      });
    }, Promise.resolve())
    .then(() => realList.slice());
};

const getLogDownloadLink = ({ commit }, { dev_id }) => {
  return fetchLogDownloadLink(dev_id);
};

const getLogData = ({ state, commit }, { dev_id, date, is_forced }) => {
  if (
    state.historyPromise[dev_id] &&
    state.historyPromise[dev_id][date] &&
    !is_forced
  ) {
    return state.historyPromise[dev_id][date];
  }
  const fetchPromise = fetchLogData(dev_id, date).then((data) => {
    commit('SET_LOG_DATA', { data, dev_id });
  });

  commit('SET_HISTORY_PROMISE', { promise: fetchPromise, dev_id, date });
  return fetchPromise;
};

const clearCachedLog = ({ commit }, { dev_id, date }) => {
  return commit('SET_HISTORY_PROMISE', { promise: null, dev_id, date });
};

const getSettingData = ({ commit }, payload) => {
  commit('SET_SETTING_DATA', payload);
};

const sendSettingData = ({ commit }, payload) => {
  return sendBatchRequest(payload);
};

const clearSettingData = ({ commit }) => {
  commit('CLEAR_SETTING_DATA');
};

const updateSettingData = ({ commit }, payload) => {
  commit('UPDATE_SETTING_DATA', payload);
};

const clearPortsData = ({ commit }, uid) => {
  commit('SET_PROMISE', { promise: null, uid });
};

const setHighlight = ({ commit }, payload) => {
  commit('SET_HIGHLIGHT', payload);
};

const clearHighlight = ({ commit }, payload) => {
  commit('CLEAR_HIGHLIGHT', payload);
};

const removePortsData = ({ commit }, uid) => {
  commit('SET_PROMISE', { promise: null, uid: uid });
  commit('REMOVE_PORTS_DATA', uid);
};

const setOpeningPopup = ({ commit }, payload) => {
  commit('SET_OPENING_POPUP', payload);
};

const getSharedPortData = ({ state, commit }, uid) => {
  if (state.sharedPromise[uid]) return state.sharedPromise[uid];
  const fetchPromise = fetchSharedPortData(uid).then((portsData) => {
    commit('SET_SHARED_PORTS_DATA', { data: [portsData], uid });
  });
  commit('SET_SHARED_PROMISE', { promise: fetchPromise, uid: uid });
  return fetchPromise;
};

const getSharedPortGroupData = ({ commit }, { list, viewId }) => {
  const listRequest = list.map((port) => fetchSharedPortData(port, true));
  const fetchPromise = sendBatchRequest(
    listRequest.map(({ relative_url, method, body }) => ({
      relative_url,
      method,
      body,
    }))
  ).then((res) => {
    if (!Array.isArray(res)) {
      throw new Error(
        res && res.message ? res.message : 'INVALID_BATCH_RESPONSE'
      );
    }
    for (let i = 0; i < res.length; i++) {
      const r = res[i];
      const req = listRequest.find(
        (request) => request.relative_url === r.relative_url
      );
      if (!req) return;
      const response = Object.assign({}, req, r);
      const data =
        response.body && typeof response.body === 'string'
          ? JSON.parse(response.body)
          : response.body;

      if (response.passcode.indexOf(response.status) !== -1) {
        Logger.info('[HTTP Port res]', response);
        commit('SET_SHARED_PORTS_DATA', {
          data: [data],
          uid: data.port_id,
          viewId,
        });
      } else {
        throw new Error(
          response && data && data.error
            ? data.error.message || 'INVALID_RESPONSE' + ': ' + data.error.code
            : 'INVALID_RESPONSE'
        );
      }
    }
  });
  return fetchPromise;
};

const getSharedSlaveData = ({ state, commit }, uid) => {
  if (state.sharedPromise[uid]) return state.sharedPromise[uid];
  const fetchPromise = fetchSharedPortData(uid).then((slavesData) => {
    commit('SET_SHARED_SLAVES_DATA', { data: [slavesData], uid });
  });
  commit('SET_SHARED_PROMISE', { promise: fetchPromise, uid: uid });
  return fetchPromise;
};

const changeModbusConfig = ({ commit }, { dev_id, dev_model, data }) => {
  return updateModbusData(dev_model, dev_id, data).then((res) => {
    commit('SET_PORTS_DATA', { data: [res], uid: dev_id });
  });
};

const getModbusSlaveConfig = ({ commit }, dev_id) => {
  const fetchPromise = fetchModbusSlaveData(dev_id).then((res) => {
    commit('SET_MODBUSSLAVE_DATA', { dev_id, data: res });
  });
  return fetchPromise;
};

const changeModbusSlaveConfig = ({ commit }, { dev_id, port_id, data }) => {
  Logger.info('changeModbusSlaveConfig', data);
  const { port_id_dec, ...rest } = data;

  return updateModbusSlaveData(dev_id, port_id, rest).then((res) => {
    commit('SET_MODBUSSLAVE_DATA', { dev_id, data: res });
  });
};

const addModbusSlaveConfig = ({ commit }, { dev_id, data }) => {
  Logger.info('addModbusSlaveConfig', data);
  return pushModbusSlaveData(dev_id, data).then((res) => {
    commit('SET_MODBUSSLAVE_DATA', { dev_id, data: res });
  });
};

const deleteModbusSlaveConfig = ({ commit }, { dev_id, port_id }) => {
  return removeModbusSlaveData(dev_id, port_id).then(() => {
    commit('REMOVE_MODBUSSLAVE_DATA', { dev_id, port_id });
  });
};

const setPendingValidation = ({ commit }, value) => {
  commit('SET_PENDING_VALIDATION', value);
};

const clearPendingValidation = ({ commit }) => {
  commit('CLEAR_PENDING_VALIDATION');
};

export default {
  getPortsData,
  getSettingData,
  clearSettingData,
  updateSettingData,
  clearPortsData,
  removePortsData,
  setHighlight,
  clearHighlight,
  setOpeningPopup,
  getPortGroup,
  getSharedPortData,
  getSharedPortGroupData,
  getSharedSlaveData,
  changeModbusConfig,
  sendSettingData,
  getModbusSlaveConfig,
  changeModbusSlaveConfig,
  addModbusSlaveConfig,
  deleteModbusSlaveConfig,
  getLogDownloadLink,
  getLogData,
  clearCachedLog,
  setPendingValidation,
  clearPendingValidation,
};
